import React, { useState } from "react"
import { TouchableOpacity, StyleSheet } from "~/ui/primitives"
import styled, { useTheme } from "~/ui/styled"
import { ColumnFlex, BetweenFlex, StartFlex, Text, Button, IconFont, Hr } from "~components/El"
import RNOTPInput from "~components/VerifyCodeInput/components/RNOTPInput"
import { usePopup } from "~hooks/usePopup"
import { globalToast } from "~utils"

export type GoogleVerificationPopupProps = {
  onVerify: (code: string) => Promise<void>
}

const GoogleVerificationPopup: React.FC<GoogleVerificationPopupProps> = ({
  onVerify,
}) => {
  const theme = useTheme()
  const popup = usePopup()
  const [code, setCode] = useState("")
  const [loading, setLoading] = useState(false)
  const [isInvalid, setIsInvalid] = useState(false)


  const handleClose = () => {
    popup.close()
  }

  const handleConfirm = async () => {
    if (code.length !== 6) {
      setIsInvalid(true)
      setTimeout(() => setIsInvalid(false), 800)
      return
    }
    setLoading(true)
    try {
      await onVerify(code)
    } catch (e: any) {
      globalToast.show(e?.message || "验证失败")
    } finally {
      setLoading(false)
    }
  }

  const busy = loading

  return (
    <Shell>
      <BetweenFlex width="100%" alignItems="center" padding="16px 16px 12px">
        <StartFlex>
          <Text fontSize={theme.fontSizes.xl} fontWeight="700" color={theme.colors.text} ml="10px">
            双重验证
          </Text>
        </StartFlex>
        <TouchableOpacity onClick={handleClose} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
          <IconFont name="close" size={16} color={theme.colors.secondary} />
        </TouchableOpacity>
      </BetweenFlex>
      <Hr />

      <ColumnFlex width="100%" padding="20px 16px 16px" alignItems="center">
        <Text fontSize={theme.fontSizes.lg} fontWeight="600" color={theme.colors.text}>
          检测到你开启了双重验证
        </Text>
        <Text mt="16px" fontSize={theme.fontSizes.sm} color={theme.colors.secondaryText}>
          请输入6位谷歌验证码
        </Text>

        <ColumnFlex width="100%" mt="14px" padding="0 4px">
          <RNOTPInput
            length={6}
            value={code}
            onChangeText={setCode}
            isInvalid={isInvalid}
            cellStyle={{ width: 44, height: 48, borderRadius: 8 }}
            autoFocus
          />
        </ColumnFlex>
      </ColumnFlex>

      <BetweenFlex width="100%" padding="12px 16px 18px" gap="12px">
        <OutlineButton flex={1} height="44px" borderRadius="10px" onClick={handleClose} disabled={busy}>
          <Text fontSize={theme.fontSizes.lg} color={theme.colors.text}>
            取消
          </Text>
        </OutlineButton>
        <SolidButton
          flex={1}
          height="44px"
          borderRadius="10px"
          loading={busy}
          disabled={busy || code.length !== 6}
          loadingText="验证中"
          onClick={handleConfirm}
        >
          <Text fontSize={theme.fontSizes.lg} fontWeight="600" color="#fff">
            确认验证
          </Text>
        </SolidButton>
      </BetweenFlex>
    </Shell>
  )
}

const Shell = styled(ColumnFlex)`
  background-color: #fff;
  border-radius: 16px;
  overflow: hidden;
  width: 100%;
`

const OutlineButton = styled(Button)`
  background-color: #fff;
  border-width: ${StyleSheet.hairlineWidth}px;
  border-color: ${({ theme }) => theme.colors.border};
  border-style: solid;
`

const SolidButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.primary};
`

export default GoogleVerificationPopup
